export enum CommonPaths {
  Home = "/",
  Login = "/login",
  Activated = "/activated",
  Logout = "/logout",
  Welcome = "/welcome",
  NotFound = "/notFound",
  OktaAuth = "/login/by-auth-code",
  SelectOrg = "/select-organisation",
  Redirect = "/redirect",
  ActivationNextStep = "/activation",
  RegisterOkta = "/register-okta",
}

export enum LPAuthPaths {
  Login = "/login",
  InvitationCheck = "/invitation/check",
  InvestorInvestorInvitationCheck = "/investor-investor/invitation/check",
}

export enum GPAuthPaths {
  InvitationCheck = "/invitation/check",
  WorkbenchWorkbenchInvitationCheck = "/workbench-workbench/invitation/check",
  WorkbenchInvestorInvitationCheck = "/workbench-investor/invitation/check",
}

export enum VegaModulesPaths {
  CapitalActivity = "/capital-activity",
  // GP workbench investor admin page (route mounted by gp-portal).
  InvestorDashboard = "/investors/:investorId/dashboard",
  FundDetailsOpportunity = "/opportunity/:opportunityId",
  InvestorDocuments = "/investor-documents",
  Offerings = "/offerings",
  Settings = "/settings",
  SettingsPersonalDetails = "/settings/personal-details",
  SettingsDisplay = "/settings/display",
  SettingsNotifications = "/settings/notifications",
  SettingsSupport = "/settings/support",
  ResourcesRoot = "/resources",
  ResourcesCms = "/resources-cms",
  SettingsRoot = "/settings/*",
  SettingsUser = "/settings/users/:id",
  Subscribe = "/subscription/:subscriptionId/questionnaire/:questionnaireId",
  SubscribeStart = "/subscription/start/:fundId/:opportunityId",
  SubscriptionContinue = "/subscription/status/:subscriptionId/opportunity/:opportunityId/",
  FundSubscriptionQuestionnaire = "/funds/:fundId/subscriptions/:subscriptionId/questionnaire",
  Reporting = "/reporting/:investmentId",
  CoInvestments = "/co-investments",
  CaseDetailByNumber = "/cases/:caseNumber",
}

export enum GPSettingsPaths {
  CaseManagement = "case-management",
  CASManagement = "cas-management",
  Collaborators = "collaborators",
  CoInvestment = "co-investment",
  Display = "display",
  Notifications = "notifications",
  Profile = "profile",
  ReportingApiKeys = "reporting-api-keys",
  Support = "support",
}

export const GPSettingsFullPaths = {
  SettingsCaseManagement: `/settings/${GPSettingsPaths.CaseManagement}`,
  SettingsCASManagement: `/settings/${GPSettingsPaths.CASManagement}`,
  SettingsCollaborators: `/settings/${GPSettingsPaths.Collaborators}`,
  SettingsCoInvestment: `/settings/${GPSettingsPaths.CoInvestment}`,
  SettingsDisplay: `/settings/${GPSettingsPaths.Display}`,
  SettingsNotifications: `/settings/${GPSettingsPaths.Notifications}`,
  SettingsProfile: `/settings/${GPSettingsPaths.Profile}`,
  SettingsReportingApiKeys: `/settings/${GPSettingsPaths.ReportingApiKeys}`,
  SettingsSupport: `/settings/${GPSettingsPaths.Support}`,
};

export enum VegaModulesWorkflowTasksPaths {
  SubscriptionTasks = "/subscription/:subscriptionId/tasks",
  SubscriptionTask = "/subscription/:subscriptionId/tasks/:workflowTaskId",
  SubscriptionTaskContactReview = "/subscription/:subscriptionId/tasks/:workflowTaskId/contacts/:contactOrder/review",
  SubscriptionTaskInvestmentReview = "/subscription/:subscriptionId/tasks/:workflowTaskId/investment/review",
  SubscriptionTaskInvestingEntityReview = "/subscription/:subscriptionId/tasks/:workflowTaskId/investing-entity/review",
  SubscriptionTaskOverview = "/subscription/:subscriptionId/tasks/:workflowTaskId/overview",
  SubscriptionTaskQuestionnaire = "/subscription/:subscriptionId/tasks/:workflowTaskId/questionnaire",
  SubscriptionTaskQuestionnaireImpersonation = "/subscription/:subscriptionId/tasks/:workflowTaskId/questionnaire/:questionnaireId",
  SubscriptionTaskQuestionnaireManualSubmission = "/subscription/:subscriptionId/tasks/:workflowTaskId/manual-submission/:questionnaireId",
  SubscriptionTaskDocuments = "/subscription/:subscriptionId/tasks/:workflowTaskId/documents",
  SubscriptionTaskApprovals = "/subscription/:subscriptionId/tasks/:workflowTaskId/approvals",
  SubscriptionTaskActivity = "/subscription/:subscriptionId/tasks/:workflowTaskId/activity",
  SubscriptionTaskNotes = "/subscription/:subscriptionId/tasks/:workflowTaskId/notes",
  SubscriptionTaskVegaIntelligence = "/subscription/:subscriptionId/tasks/:workflowTaskId/vega-intelligence",
}

export enum CoInvestmentPaths {
  Details = ":id/details",
  DataRoom = ":id/data-room",
  NdaManagement = ":id/nda-management",
  Prospects = ":id/prospects",
  ProspectsFindFromSalesforce = ":id/prospects/import",
  ProspectsAddNotes = ":id/prospects/add-notes",
  Outreach = ":id/outreach",
  Tracking = ":id/tracking",
  Audit = ":id/audit",
  LpNdas = ":id/data-room/lp-ndas",
  ShortCode = "opp/:shortCode",
  // New navigation paths (VD-25769)
  Configure = ":id/configure",
  EmailTemplates = ":id/configure/email-templates",
  Share = ":id/share",
  SendDrafts = ":id/share/send-drafts",
  Track = ":id/track",
  Report = ":id/report",
  // Cross-deal insights dashboard (VD-26167)
  Insights = "insights",
  // LP profile within insights (VD-26169)
  InsightsLimitedPartner = "insights/limited-partners/:limitedPartnerId",
  // Co-invest notification centre (VD-28286)
  Notifications = "notifications",
  // Co-invest GP messaging (VD-28584)
  Messages = "messages",
  Conversation = "messages/:conversationId",
}

export enum CoInvestmentLpPaths {
  Overview = ":id/overview",
  DataRoom = ":id/data-room",
  Terms = ":id/terms",
  PerformanceReport = ":id/performance-report",
  Details = ":id/details",
  Notifications = "notifications",
  Insights = "insights",
  // Co-invest LP messaging (VD-28584)
  Messages = "messages",
  Conversation = "messages/:conversationId",
}

// Full co-investment paths
export enum CoInvestmentFullPaths {
  // Base path
  CoInvestments = "/co-investments",
  // GP Portal
  CoInvestmentDetails = `/co-investments/${CoInvestmentPaths.Details}`,
  CoInvestmentDataRoom = `/co-investments/${CoInvestmentPaths.DataRoom}`,
  CoInvestmentNdaManagement = `/co-investments/${CoInvestmentPaths.NdaManagement}`,
  CoInvestmentProspects = `/co-investments/${CoInvestmentPaths.Prospects}`,
  CoInvestmentProspectsFindFromSalesforce = `/co-investments/${CoInvestmentPaths.ProspectsFindFromSalesforce}`,
  CoInvestmentProspectsAddNotes = `/co-investments/${CoInvestmentPaths.ProspectsAddNotes}`,
  CoInvestmentOutreach = `/co-investments/${CoInvestmentPaths.Outreach}`,
  CoInvestmentTracking = `/co-investments/${CoInvestmentPaths.Tracking}`,
  CoInvestmentAudit = `/co-investments/${CoInvestmentPaths.Audit}`,
  CoInvestmentLpNdas = `/co-investments/${CoInvestmentPaths.LpNdas}`,
  CoInvestmentConfigure = `/co-investments/${CoInvestmentPaths.Configure}`,
  CoInvestmentEmailTemplates = `/co-investments/${CoInvestmentPaths.EmailTemplates}`,
  CoInvestmentShare = `/co-investments/${CoInvestmentPaths.Share}`,
  CoInvestmentShareSendDrafts = `/co-investments/${CoInvestmentPaths.SendDrafts}`,
  CoInvestmentTrack = `/co-investments/${CoInvestmentPaths.Track}`,
  CoInvestmentReport = `/co-investments/${CoInvestmentPaths.Report}`,
  CoInvestmentInsights = `/co-investments/${CoInvestmentPaths.Insights}`,
  CoInvestmentInsightsLimitedPartner = `/co-investments/${CoInvestmentPaths.InsightsLimitedPartner}`,
  CoInvestmentNotifications = `/co-investments/${CoInvestmentPaths.Notifications}`,
  CoInvestmentMessages = `/co-investments/${CoInvestmentPaths.Messages}`,
  CoInvestmentConversation = `/co-investments/${CoInvestmentPaths.Conversation}`,
  // LP Portal
  CoInvestmentLpOverview = `/co-investments/${CoInvestmentLpPaths.Overview}`,
  CoInvestmentLpDataRoom = `/co-investments/${CoInvestmentLpPaths.DataRoom}`,
  CoInvestmentLpTerms = `/co-investments/${CoInvestmentLpPaths.Terms}`,
  CoInvestmentLpPerformanceReport = `/co-investments/${CoInvestmentLpPaths.PerformanceReport}`,
  CoInvestmentLpDetails = `/co-investments/${CoInvestmentLpPaths.Details}`,
  CoInvestmentLpNotifications = `/co-investments/${CoInvestmentLpPaths.Notifications}`,
  CoInvestmentLpInsights = `/co-investments/${CoInvestmentLpPaths.Insights}`,
  CoInvestmentLpMessages = `/co-investments/${CoInvestmentLpPaths.Messages}`,
  CoInvestmentLpConversation = `/co-investments/${CoInvestmentLpPaths.Conversation}`,
  // Short code
  CoInvestmentsShortCode = `${CoInvestmentPaths.ShortCode}`, // omit `/co-investments` to make it shorter
}
